/**
 * PlanTab.jsx — Open House Planner plan tab (/openhouse?tab=plan)
 *
 * Pick/create a day plan, edit its basics (date, depart time, start address,
 * dwell minutes), order the stops, then run the time-window route optimizer
 * (POST /openhouse/route). The optimized schedule is written to plan.route
 * and mirrored onto each stop (position + eta/etd), rendered as a vertical
 * itinerary + numbered pins on the shared OpenHouseMap.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Stack, Typography, Button, IconButton, Chip, TextField, Select, MenuItem,
  FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Paper, Alert, Tooltip, Divider,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Route as RouteIcon,
  PlayArrow as PlayArrowIcon,
  DirectionsCar as DirectionsCarIcon,
  CheckCircle as CheckCircleIcon,
  HourglassTop as HourglassTopIcon,
  ErrorOutline as ErrorOutlineIcon,
  OpenInNew as OpenInNewIcon,
  EventNote as EventNoteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useJsApiLoader } from '@react-google-maps/api';
import {
  useOpenHouses, useUpdateOpenHouse, useOhPlans, useCreateOhPlan, useUpdateOhPlan,
  useDeleteOhPlan, useOhPlanStops, useAllOhPlanStops, useUpdateOhPlanStop,
  useDeleteOhPlanStop, useOptimizeRoute,
} from '../../hooks/useOpenHouses';
import { useAuth } from '../../hooks/useAuth';
import { useMobile } from '../../hooks/useMobile';
import { useFocus } from '../../hooks/useFocus';
import { useToast } from '../layout/Toast';
import { logAction } from '../../lib/actionLogger';
import { fmtD } from '../../lib/utils';
import AddressAutocomplete from '../shared/AddressAutocomplete';
import OpenHouseMap from './OpenHouseMap';
import OpenHouseDetailModal from './OpenHouseDetailModal';
import { geocodeAddress, toPtIso, DEFAULT_PLAN_START } from './geo';

const MAPS_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_KEY;
const LIBRARIES = ['places'];

const PT = 'America/Los_Angeles';
const VILLAGE_COLOR = '#E879F9';
const ROUTED_COLOR = '#3B82F6';
const UNROUTED_COLOR = '#9CA3AF';
const ORIGIN_COLOR = '#10B981';
const DWELL_OPTIONS = [15, 20, 25, 30];
const TIMING_OPTIONS = [
  { value: 'anytime', label: 'Anytime in window' },
  { value: 'open', label: 'At open (before crowd)' },
  { value: 'close', label: 'Before close (after crowd)' },
  { value: 'edges', label: 'Both edges (auto)' },
];
const SLOT_LABEL = { open: 'at open', close: 'before close', window: '', null: '' };
const STATUS_COLOR = { draft: 'default', active: 'success', done: 'info' };

// ── PT time helpers ─────────────────────────────────────────────────────

/** ISO → '1 PM' / '1:30 PM' in Pacific time. */
function fmtPtTime(iso) {
  if (!iso) return '';
  return new Date(iso)
    .toLocaleTimeString('en-US', { timeZone: PT, hour: 'numeric', minute: '2-digit' })
    .replace(':00', '');
}

/** ISO → 'HH:mm' (24h) in Pacific time, for <input type="time">. */
function ptTimeHHmm(iso) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: PT, hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(iso));
}

/** '13:30' (input type=time) → '1:30 PM' for toPtIso. */
function time24To12(t) {
  const [h, m] = String(t || '').split(':').map(Number);
  if (isNaN(h)) return '';
  const period = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${period}`;
}

/** OH window: '1–4 PM' in Pacific time. */
function fmtWindow(startIso, endIso) {
  if (!startIso) return '—';
  const s = fmtPtTime(startIso);
  if (!endIso) return s;
  const e = fmtPtTime(endIso);
  const sameSuffix = s.slice(-2) === e.slice(-2);
  return `${sameSuffix ? s.slice(0, -3) : s}–${e}`;
}

/** Compact price: '$2.4M', '$950K'. */
function fmtPrice(p) {
  const n = parseFloat(p);
  if (!n || isNaN(n)) return '';
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
}

function isVillage(brokerage) {
  return (brokerage || '').toLowerCase().includes('village');
}

function ptTodayKey() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: PT, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

// ── New plan dialog ─────────────────────────────────────────────────────

function NewPlanDialog({ open, onClose, onCreated }) {
  const { profile } = useAuth();
  const { toast } = useToast();
  const createPlan = useCreateOhPlan();

  const [title, setTitle] = useState('');
  const [planDate, setPlanDate] = useState('');
  const [departTime, setDepartTime] = useState('10:30');
  const [startAddress, setStartAddress] = useState(DEFAULT_PLAN_START.address);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setPlanDate(ptTodayKey());
    setDepartTime('10:30');
    setStartAddress(DEFAULT_PLAN_START.address);
    setSaving(false);
  }, [open]);

  const handleCreate = async () => {
    if (!planDate) { toast('Pick a date', 'error'); return; }
    setSaving(true);
    try {
      let coords = { lat: null, lng: null };
      if (startAddress.trim() === DEFAULT_PLAN_START.address) {
        coords = { lat: DEFAULT_PLAN_START.lat, lng: DEFAULT_PLAN_START.lng };
      } else if (startAddress.trim()) {
        try { coords = await geocodeAddress(startAddress); } catch { /* geocode later */ }
      }
      const plan = await createPlan.mutateAsync({
        title: title.trim() || `${fmtD(planDate)} circuit`,
        planDate,
        departAt: departTime ? toPtIso(planDate, time24To12(departTime)) : null,
        startAddress: startAddress.trim() || null,
        startLat: coords.lat,
        startLng: coords.lng,
        status: 'draft',
        data: { dwellMin: 20 },
        createdBy: profile?.id || null,
      });
      logAction({
        action: 'Create OH Plan',
        user: profile?.full_name || profile?.email,
        description: `Created open house plan "${plan.title}" for ${fmtD(planDate)}`,
        details: { planId: plan.id },
      });
      toast('Plan created — add stops from the Browse tab', 'success');
      onCreated?.(plan.id);
      onClose();
    } catch (e) {
      toast(`Failed to create plan: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>New plan</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <TextField
            size="small"
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Saturday SB circuit"
            fullWidth
            autoFocus
          />
          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              type="date"
              label="Date"
              value={planDate}
              onChange={(e) => setPlanDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              type="time"
              label="Depart"
              value={departTime}
              onChange={(e) => setDepartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Stack>
          <AddressAutocomplete
            variant="mui"
            label="Start address"
            placeholder="Where the day starts"
            value={startAddress}
            onChange={setStartAddress}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={saving || !planDate}
          startIcon={saving ? <CircularProgress size={14} /> : <AddIcon />}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Itinerary timeline ──────────────────────────────────────────────────

function Itinerary({ plan, route, rowByStopId, gmapsUrl }) {
  const legs = (route.order || [])
    .map((stopId) => ({
      sched: (route.schedule || []).find((s) => s.id === stopId),
      row: rowByStopId[stopId],
    }))
    .filter((l) => l.sched && l.row?.oh);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>Itinerary</Typography>
        {gmapsUrl && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DirectionsCarIcon />}
            component="a"
            href={gmapsUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in Google Maps
          </Button>
        )}
      </Stack>

      <Chip
        size="small"
        color="success"
        icon={<PlayArrowIcon />}
        label={`Depart ${fmtPtTime(plan.departAt)}${plan.startAddress ? ` · ${plan.startAddress}` : ''}`}
        sx={{ maxWidth: '100%', mb: 0.5 }}
      />

      <Box sx={{ borderLeft: '2px solid', borderColor: 'divider', ml: 1.5, pl: 2 }}>
        {legs.map(({ sched, row }, i) => (
          <Box key={sched.id} sx={{ py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              <DirectionsCarIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'text-top' }} />
              {Math.round(sched.driveMinFromPrev || 0)} min drive
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
              Arrive {fmtPtTime(sched.arriveAt)}
              {(sched.waitMin || 0) > 0 && (
                <Typography component="span" variant="caption" sx={{ color: 'warning.main', ml: 0.75 }}>
                  {Math.round(sched.waitMin)} min early
                </Typography>
              )}
            </Typography>
            <Paper variant="outlined" sx={{ p: 1, my: 0.5, borderColor: sched.withinWindow === false ? 'error.main' : 'divider' }}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight={600}>
                  {i + 1}. {row.oh.address}
                </Typography>
                {SLOT_LABEL[sched.slot] && (
                  <Chip
                    size="small"
                    label={`shoot ${SLOT_LABEL[sched.slot]}`}
                    color={sched.slot === 'open' ? 'info' : 'secondary'}
                    sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                  />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {[fmtWindow(row.oh.startAt, row.oh.endAt), row.oh.agentName, row.oh.brokerage].filter(Boolean).join(' · ')}
              </Typography>
            </Paper>
            <Typography variant="caption" color="text.secondary">
              Leave {fmtPtTime(sched.departAt)}
            </Typography>
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 1 }} />
      <Typography variant="body2" fontWeight={600}>
        {route.totalDriveMin != null && `${Math.round(route.totalDriveMin)} min total driving`}
        {route.finishAt && ` · done by ${fmtPtTime(route.finishAt)}`}
      </Typography>
    </Paper>
  );
}

// ── Main tab ────────────────────────────────────────────────────────────

export default function PlanTab({ planId, onSelectPlan, onRun }) {
  const { isMobile } = useMobile();
  const { toast } = useToast();
  const { profile } = useAuth();
  const { isHidden } = useFocus();

  const { data: plans = [], isLoading: plansLoading } = useOhPlans();
  const runHidden = isHidden('openhouse.runFollowup') && !plans.some((p) => p.status && p.status !== 'draft');
  const { data: openHouses = [] } = useOpenHouses();
  const { data: allStops = [] } = useAllOhPlanStops();
  const updatePlan = useUpdateOhPlan();
  const deletePlan = useDeleteOhPlan();
  const updateStop = useUpdateOhPlanStop();
  const deleteStop = useDeleteOhPlanStop();
  const updateOh = useUpdateOpenHouse();
  const optimizeMut = useOptimizeRoute();

  // Load Maps JS so geocoding works even before the map renders.
  // Options must be identical everywhere useJsApiLoader is called.
  useJsApiLoader({
    id: 'rfos-google-maps',
    googleMapsApiKey: MAPS_API_KEY || '',
    libraries: LIBRARIES,
  });

  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => String(b.planDate || '').localeCompare(String(a.planDate || ''))),
    [plans],
  );
  const stopCountByPlan = useMemo(() => {
    const counts = {};
    for (const s of allStops) counts[s.planId] = (counts[s.planId] || 0) + 1;
    return counts;
  }, [allStops]);

  const plan = useMemo(() => plans.find((p) => p.id === planId) || null, [plans, planId]);

  // Default: most recent non-done plan when nothing (valid) is selected
  useEffect(() => {
    if (plansLoading || plan || sortedPlans.length === 0) return;
    const def = sortedPlans.find((p) => p.status !== 'done') || sortedPlans[0];
    if (def) onSelectPlan?.(def.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plansLoading, plan, sortedPlans]);

  const { data: stops = [] } = useOhPlanStops(plan?.id);
  const sortedStops = useMemo(
    () => [...stops].sort((a, b) => (a.position ?? 1e9) - (b.position ?? 1e9)),
    [stops],
  );
  const ohById = useMemo(() => Object.fromEntries(openHouses.map((oh) => [oh.id, oh])), [openHouses]);
  const rows = useMemo(
    () => sortedStops.map((stop) => ({ stop, oh: ohById[stop.openHouseId] })),
    [sortedStops, ohById],
  );
  const rowByStopId = useMemo(() => Object.fromEntries(rows.map((r) => [r.stop.id, r])), [rows]);

  const route = plan?.route && Array.isArray(plan.route.order) ? plan.route : null;
  const schedByStopId = useMemo(
    () => Object.fromEntries((route?.schedule || []).map((s) => [s.id, s])),
    [route],
  );
  const orderIndex = useMemo(() => {
    const idx = {};
    (route?.order || []).forEach((id, i) => { idx[id] = i; });
    return idx;
  }, [route]);
  const droppedSet = useMemo(() => new Set(route?.droppedStops || []), [route]);
  const dwellMin = plan?.data?.dwellMin ?? 20;
  const timingMode = plan?.data?.timingMode || 'anytime';

  // ── Editor draft (commit on blur, not per keystroke) ──
  const [draft, setDraft] = useState({ title: '', planDate: '', departTime: '10:30', startAddress: '' });
  useEffect(() => {
    if (!plan) return;
    setDraft({
      title: plan.title || '',
      planDate: plan.planDate ? String(plan.planDate).slice(0, 10) : '',
      departTime: plan.departAt ? ptTimeHHmm(plan.departAt) : '10:30',
      startAddress: plan.startAddress || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.id]);

  const commitPlan = (fields) => {
    if (!plan || Object.keys(fields).length === 0) return;
    updatePlan.mutate({ id: plan.id, ...fields }, {
      onError: (e) => toast(`Save failed: ${e.message}`, 'error'),
    });
  };

  const commitTitle = () => {
    if (plan && draft.title.trim() && draft.title !== (plan.title || '')) {
      commitPlan({ title: draft.title.trim() });
    }
  };

  const commitSchedule = () => {
    if (!plan || !draft.planDate || !draft.departTime) return;
    const curDate = plan.planDate ? String(plan.planDate).slice(0, 10) : '';
    const curTime = plan.departAt ? ptTimeHHmm(plan.departAt) : '';
    if (draft.planDate === curDate && draft.departTime === curTime) return;
    const fields = { planDate: draft.planDate };
    try { fields.departAt = toPtIso(draft.planDate, time24To12(draft.departTime)); } catch { /* keep old departAt */ }
    commitPlan(fields);
  };

  // Start address: commit (with geocode) shortly after typing/selection settles
  const addrTimer = useRef(null);
  const handleAddressChange = (text) => {
    setDraft((d) => ({ ...d, startAddress: text }));
    clearTimeout(addrTimer.current);
    addrTimer.current = setTimeout(async () => {
      if (!plan || text === (plan.startAddress || '') || text.trim().length < 5) return;
      let lat = null;
      let lng = null;
      try { ({ lat, lng } = await geocodeAddress(text)); } catch { /* coords resolved at optimize time */ }
      commitPlan({ startAddress: text.trim(), startLat: lat, startLng: lng });
    }, 1200);
  };
  useEffect(() => () => clearTimeout(addrTimer.current), []);

  // Geocode on demand when address exists but coords are missing
  const originGeoTried = useRef({});
  useEffect(() => {
    if (!plan?.startAddress || (plan.startLat != null && plan.startLng != null)) return;
    if (originGeoTried.current[plan.id]) return;
    originGeoTried.current[plan.id] = true;
    geocodeAddress(plan.startAddress)
      .then(({ lat, lng }) => updatePlan.mutate({ id: plan.id, startLat: lat, startLng: lng }))
      .catch(() => { /* resolved again at optimize time */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.id, plan?.startAddress, plan?.startLat, plan?.startLng]);

  // ── Dialogs ──
  const [newOpen, setNewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [focusStopId, setFocusStopId] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [detailOh, setDetailOh] = useState(null);

  const handleDelete = () => {
    if (!plan) return;
    const title = plan.title;
    deletePlan.mutate(plan.id, {
      onSuccess: () => {
        logAction({
          action: 'Delete OH Plan',
          user: profile?.full_name || profile?.email,
          description: `Deleted open house plan "${title}"`,
          details: { planId: plan.id },
        });
        toast(`Deleted "${title}"`, 'info');
        setDeleteOpen(false);
        onSelectPlan?.(null);
      },
      onError: (e) => toast(`Delete failed: ${e.message}`, 'error'),
    });
  };

  // ── Stop ops ──
  const moveStop = async (index, dir) => {
    const a = sortedStops[index];
    const b = sortedStops[index + dir];
    if (!a || !b) return;
    try {
      await updateStop.mutateAsync({ id: a.id, position: index + dir });
      await updateStop.mutateAsync({ id: b.id, position: index });
    } catch (e) {
      toast(`Reorder failed: ${e.message}`, 'error');
    }
  };

  const removeStop = (stop, oh) => {
    deleteStop.mutate(stop.id, {
      onSuccess: () => toast(`Removed ${oh?.address || 'stop'}`, 'info'),
      onError: (e) => toast(`Remove failed: ${e.message}`, 'error'),
    });
  };

  // ── Optimize ──
  const handleOptimize = async () => {
    if (!plan) return;
    if (rows.length < 2) { toast('Add at least 2 stops (Browse tab → Add to plan)', 'error'); return; }
    const missingWindow = rows.filter((r) => !r.oh?.startAt || !r.oh?.endAt);
    if (missingWindow.length > 0) {
      toast(`${missingWindow.length} stop${missingWindow.length === 1 ? ' is' : 's are'} missing an open-house time window`, 'error');
      return;
    }
    setOptimizing(true);
    try {
      // Origin coords (geocode on demand)
      let origin = { lat: parseFloat(plan.startLat), lng: parseFloat(plan.startLng) };
      if (plan.startLat == null || plan.startLng == null) {
        if (!plan.startAddress) { toast('Set a start address first', 'error'); return; }
        origin = await geocodeAddress(plan.startAddress);
        await updatePlan.mutateAsync({ id: plan.id, startLat: origin.lat, startLng: origin.lng });
      }
      // Stop coords (geocode + persist missing ones)
      const stopPayload = [];
      for (const r of rows) {
        let lat = r.oh.lat;
        let lng = r.oh.lng;
        if (lat == null || lng == null) {
          const full = [r.oh.address, r.oh.city, 'CA'].filter(Boolean).join(', ');
          ({ lat, lng } = await geocodeAddress(full));
          await updateOh.mutateAsync({ id: r.oh.id, lat, lng });
        }
        stopPayload.push({
          id: r.stop.id,
          lat: parseFloat(lat),
          lng: parseFloat(lng),
          windowStart: r.oh.startAt,
          windowEnd: r.oh.endAt,
        });
      }
      const departAt = toPtIso(
        draft.planDate || String(plan.planDate).slice(0, 10),
        time24To12(draft.departTime || '10:30'),
      );
      const res = await optimizeMut.mutateAsync({ origin, departAt, dwellMin, timingMode, stops: stopPayload });

      const newRoute = {
        order: res.order || [],
        schedule: res.schedule || [],
        droppedStops: res.droppedStops || [],
        totalDriveMin: res.totalDriveMin ?? null,
        finishAt: res.finishAt || null,
        path: res.path || [],
        warnings: res.warnings || [],
        optimizedAt: new Date().toISOString(),
      };
      await updatePlan.mutateAsync({ id: plan.id, route: newRoute });

      // Mirror order + schedule onto the stops
      const schedById = Object.fromEntries(newRoute.schedule.map((s) => [s.id, s]));
      let pos = 0;
      for (const stopId of newRoute.order) {
        const sched = schedById[stopId];
        await updateStop.mutateAsync({
          id: stopId,
          position: pos++,
          eta: sched?.arriveAt || null,
          etd: sched?.departAt || null,
        });
      }
      for (const stopId of newRoute.droppedStops) {
        await updateStop.mutateAsync({ id: stopId, position: pos++, eta: null, etd: null });
      }

      const parts = [
        `${newRoute.order.length} stops`,
        newRoute.totalDriveMin != null && `${Math.round(newRoute.totalDriveMin)} min driving`,
        newRoute.finishAt && `done by ${fmtPtTime(newRoute.finishAt)}`,
      ].filter(Boolean);
      if (newRoute.droppedStops.length > 0) {
        toast(`${parts.join(', ')} — ${newRoute.droppedStops.length} dropped (can't fit)`, 'info');
      } else {
        toast(parts.join(', '), 'success');
      }
      logAction({
        action: 'Optimize OH Route',
        user: profile?.full_name || profile?.email,
        description: `Optimized "${plan.title}" — ${parts.join(', ')}`,
        details: { planId: plan.id, stops: newRoute.order.length, dropped: newRoute.droppedStops.length, totalDriveMin: newRoute.totalDriveMin },
      });
    } catch (e) {
      toast(`Optimize failed: ${e.message}`, 'error');
    } finally {
      setOptimizing(false);
    }
  };

  const handleStartRun = () => {
    if (!plan) return;
    updatePlan.mutate({ id: plan.id, status: 'active' }, {
      onSuccess: () => {
        logAction({
          action: 'Start OH Run',
          user: profile?.full_name || profile?.email,
          description: `Started run for "${plan.title}"`,
          details: { planId: plan.id },
        });
        onRun?.(plan.id);
      },
      onError: (e) => toast(`Failed to start: ${e.message}`, 'error'),
    });
  };

  // ── Google Maps directions URL ──
  const gmapsUrl = useMemo(() => {
    if (!route?.order?.length || !plan || plan.startLat == null || plan.startLng == null) return null;
    const coords = route.order
      .map((id) => rowByStopId[id]?.oh)
      .filter((oh) => oh && oh.lat != null && oh.lng != null)
      .map((oh) => `${oh.lat},${oh.lng}`);
    if (coords.length === 0) return null;
    const origin = `${plan.startLat},${plan.startLng}`;
    const destination = coords[coords.length - 1];
    const waypoints = coords.slice(0, -1).join('|');
    return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}`
      + `&destination=${encodeURIComponent(destination)}`
      + (waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : '')
      + '&travelmode=driving';
  }, [route, plan, rowByStopId]);

  // ── Map markers ──
  const markers = useMemo(() => {
    const list = rows
      .filter((r) => r.oh && r.oh.lat != null && r.oh.lng != null)
      .map((r) => {
        const idx = orderIndex[r.stop.id];
        return {
          id: r.stop.id,
          lat: r.oh.lat,
          lng: r.oh.lng,
          label: idx != null ? String(idx + 1) : '?',
          color: idx != null ? ROUTED_COLOR : UNROUTED_COLOR,
          title: r.oh.address,
          subtitle: [fmtWindow(r.oh.startAt, r.oh.endAt), r.oh.agentName, r.oh.brokerage].filter(Boolean).join(' · '),
          url: r.oh.listingUrl,
        };
      });
    if (plan && plan.startLat != null && plan.startLng != null) {
      list.unshift({
        id: '__origin',
        lat: plan.startLat,
        lng: plan.startLng,
        label: 'S',
        color: ORIGIN_COLOR,
        title: 'Start',
        subtitle: plan.startAddress || '',
      });
    }
    return list;
  }, [rows, orderIndex, plan]);

  // ── Feasibility icon per row ──
  const feasibility = (stopId) => {
    if (droppedSet.has(stopId)) {
      return { icon: <ErrorOutlineIcon color="error" sx={{ fontSize: 18 }} />, tip: "Dropped — can't fit in the windows" };
    }
    const sched = schedByStopId[stopId];
    if (!sched) return null;
    if (sched.withinWindow === false) {
      return { icon: <ErrorOutlineIcon color="error" sx={{ fontSize: 18 }} />, tip: 'Arrives outside the open-house window' };
    }
    if ((sched.waitMin || 0) > 0) {
      return { icon: <HourglassTopIcon sx={{ fontSize: 18, color: 'warning.main' }} />, tip: `${Math.round(sched.waitMin)} min early — waits for doors` };
    }
    return { icon: <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />, tip: 'Within window' };
  };

  // ── Render ──
  if (!plansLoading && sortedPlans.length === 0) {
    return (
      <>
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <EventNoteIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="subtitle1" fontWeight={600}>No plans yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a plan here, then add stops from the Browse tab.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}>
            New plan
          </Button>
        </Paper>
        <NewPlanDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={(id) => onSelectPlan?.(id)} />
      </>
    );
  }

  const editorEl = plan && (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1.5 }}>
        <TextField
          size="small"
          label="Title"
          value={draft.title}
          onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
          onBlur={commitTitle}
          sx={{ flex: '1 1 220px' }}
        />
        <TextField
          size="small"
          type="date"
          label="Date"
          value={draft.planDate}
          onChange={(e) => setDraft((d) => ({ ...d, planDate: e.target.value }))}
          onBlur={commitSchedule}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 150 }}
        />
        <TextField
          size="small"
          type="time"
          label="Depart"
          value={draft.departTime}
          onChange={(e) => setDraft((d) => ({ ...d, departTime: e.target.value }))}
          onBlur={commitSchedule}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 120 }}
        />
        <FormControl size="small" sx={{ width: 110 }}>
          <InputLabel>Dwell</InputLabel>
          <Select
            label="Dwell"
            value={DWELL_OPTIONS.includes(dwellMin) ? dwellMin : 20}
            onChange={(e) => commitPlan({ data: { ...(plan.data || {}), dwellMin: e.target.value } })}
          >
            {DWELL_OPTIONS.map((m) => <MenuItem key={m} value={m}>{m} min</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 190 }}>
          <InputLabel>Shoot timing</InputLabel>
          <Select
            label="Shoot timing"
            value={timingMode}
            onChange={(e) => commitPlan({ data: { ...(plan.data || {}), timingMode: e.target.value } })}
          >
            {TIMING_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <Box sx={{ flex: '2 1 260px' }}>
          <AddressAutocomplete
            variant="mui"
            label="Start address"
            placeholder="Where the day starts"
            value={draft.startAddress}
            onChange={handleAddressChange}
          />
        </Box>
      </Stack>
    </Paper>
  );

  const stopsEl = plan && (
    <Paper variant="outlined">
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Stops ({rows.length})
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            startIcon={optimizing ? <CircularProgress size={14} color="inherit" /> : <RouteIcon />}
            onClick={handleOptimize}
            disabled={optimizing || rows.length < 2}
          >
            Optimize route
          </Button>
          {route && !runHidden && (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<PlayArrowIcon />}
              onClick={handleStartRun}
              data-focus="openhouse.runFollowup"
            >
              Start run
            </Button>
          )}
        </Stack>
      </Stack>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
          No stops yet — select open houses in the Browse tab and hit “Add to plan”.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 34 }}>#</TableCell>
                <TableCell>Address</TableCell>
                <TableCell sx={{ width: 90 }}>Window</TableCell>
                <TableCell sx={{ width: 70 }}>Price</TableCell>
                <TableCell>Agent</TableCell>
                {route && <TableCell sx={{ width: 150 }}>Schedule</TableCell>}
                <TableCell sx={{ width: 110 }} align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ stop, oh }, i) => {
                const sched = schedByStopId[stop.id];
                const feas = route ? feasibility(stop.id) : null;
                return (
                  <TableRow
                    key={stop.id}
                    hover
                    selected={focusStopId === stop.id}
                    onClick={() => setFocusStopId(stop.id)}
                    sx={{ cursor: 'pointer', opacity: droppedSet.has(stop.id) ? 0.55 : 1 }}
                  >
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{oh?.address || '(missing listing)'}</Typography>
                      <Typography variant="caption" color="text.secondary">{oh?.city || ''}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{fmtWindow(oh?.startAt, oh?.endAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontWeight={600}>{fmtPrice(oh?.price)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ display: 'block' }} noWrap>{oh?.agentName || ''}</Typography>
                      {oh?.brokerage && (
                        <Chip
                          size="small"
                          label={oh.brokerage}
                          sx={{
                            height: 16,
                            fontSize: 9,
                            maxWidth: 140,
                            ...(isVillage(oh.brokerage) ? { bgcolor: VILLAGE_COLOR, color: '#fff', fontWeight: 600 } : {}),
                          }}
                        />
                      )}
                    </TableCell>
                    {route && (
                      <TableCell>
                        {sched ? (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {feas && <Tooltip title={feas.tip}>{feas.icon}</Tooltip>}
                            <Box>
                              <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>
                                {fmtPtTime(sched.arriveAt)}→{fmtPtTime(sched.departAt)}
                                {SLOT_LABEL[sched.slot] && (
                                  <Typography component="span" variant="caption" sx={{ ml: 0.5, color: sched.slot === 'open' ? 'info.main' : 'secondary.main', fontWeight: 600 }}>
                                    · {SLOT_LABEL[sched.slot]}
                                  </Typography>
                                )}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {Math.round(sched.driveMinFromPrev || 0)} min drive
                                {(sched.waitMin || 0) > 0 && ` · ${Math.round(sched.waitMin)} min wait`}
                              </Typography>
                            </Box>
                          </Stack>
                        ) : droppedSet.has(stop.id) ? (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {feas && <Tooltip title={feas.tip}>{feas.icon}</Tooltip>}
                            <Typography variant="caption" color="error">Dropped</Typography>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" disabled={!oh} onClick={() => setDetailOh(oh)} aria-label="Preview listing">
                        <VisibilityIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" disabled={i === 0} onClick={() => moveStop(i, -1)} aria-label="Move up">
                        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" disabled={i === rows.length - 1} onClick={() => moveStop(i, 1)} aria-label="Move down">
                        <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => removeStop(stop, oh)} aria-label="Remove stop">
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );

  const warningsEl = route?.warnings?.length > 0 && (
    <Alert severity="warning">
      {route.warnings.map((w, i) => (
        <Typography key={i} variant="body2">{w}</Typography>
      ))}
    </Alert>
  );

  const itineraryEl = plan && route && route.schedule?.length > 0 && (
    <Itinerary plan={plan} route={route} rowByStopId={rowByStopId} gmapsUrl={gmapsUrl} />
  );

  const mapEl = plan && (
    <OpenHouseMap
      markers={markers}
      path={route?.path || null}
      selectedId={focusStopId}
      onMarkerClick={(id) => setFocusStopId(id)}
      height={isMobile ? 320 : 560}
    />
  );

  const leftCol = (
    <Stack spacing={2}>
      {editorEl}
      {warningsEl}
      {stopsEl}
      {itineraryEl}
    </Stack>
  );

  return (
    <Box>
      {/* Plan picker */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 260, flex: isMobile ? 1 : 'none' }}>
          <InputLabel>Plan</InputLabel>
          <Select
            label="Plan"
            value={plan?.id || ''}
            onChange={(e) => onSelectPlan?.(e.target.value)}
            renderValue={(v) => {
              const p = plans.find((x) => x.id === v);
              return p ? `${p.title || 'Untitled'} · ${fmtD(p.planDate)}` : '';
            }}
          >
            {sortedPlans.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                    {p.title || 'Untitled'} · {fmtD(p.planDate)}
                  </Typography>
                  <Chip size="small" label={p.status} color={STATUS_COLOR[p.status] || 'default'} sx={{ height: 18, fontSize: 10 }} />
                  <Typography variant="caption" color="text.secondary">
                    {stopCountByPlan[p.id] || 0} stops
                  </Typography>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}>
          New plan
        </Button>
        {plan && (
          <Tooltip title="Delete plan">
            <IconButton size="small" onClick={() => setDeleteOpen(true)} aria-label="Delete plan">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* Layout */}
      {plan && (isMobile ? (
        <Stack spacing={2}>
          {leftCol}
          {mapEl}
        </Stack>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ width: '58%' }}>{leftCol}</Box>
          <Box sx={{ width: '42%', position: 'sticky', top: 16 }}>{mapEl}</Box>
        </Box>
      ))}

      <NewPlanDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={(id) => onSelectPlan?.(id)} />

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete plan?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            “{plan?.title}” and its {rows.length} stop{rows.length === 1 ? '' : 's'} will be removed.
            The open houses themselves stay in Browse.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deletePlan.isPending}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <OpenHouseDetailModal
        oh={detailOh}
        open={!!detailOh}
        onClose={() => setDetailOh(null)}
      />
    </Box>
  );
}
